import {Injectable} from '@angular/core';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {ComponentStore} from '@ngrx/component-store';
import {LoadingController} from '@ionic/angular';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  pairwise,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs/operators';
import {BehaviorSubject, EMPTY, firstValueFrom, forkJoin, from, lastValueFrom, merge, of} from 'rxjs';
import {differenceInHours, getYear, setHours} from 'date-fns';
import {format, utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';
import roundToNearestMinutes from 'date-fns/roundToNearestMinutes';
import {AlertService} from "./alert.service";
import {LoggingService} from './logging.service';
import {AuthService} from './auth.service';
import {AppTimerService} from './app-timer.service';
import {ConnectionStatus, NetworkService} from './network.service';
import {ApiService} from './api.service';
import {StorageService} from './storage.service';
import {ToastService} from './toast.service';
import type {
  AppMode,
  EmployeeInterface,
  EntityInterface,
  LastPunchInterface,
  Project,
  PunchInterface,
  PunchTypesInterface,
  TimesheetInterface,
  TimesheetPeriodInterface,
  UserPresetsInterface,
  ValidatePunchTimesInterface,
} from 'src/app/interfaces';

export interface AppServiceState {
  entity: EntityInterface | null;
  getLocal: boolean;
  userPresets: UserPresetsInterface | null;
  lastPunch: LastPunchInterface | null;
}

@Injectable({
  providedIn: 'root',
})
export class AppService extends ComponentStore<AppServiceState> {
  appMode: AppMode;
  isOnline: boolean;
  private overlays: Promise<any>[] = [];
  private periodsLoadingState$ = new BehaviorSubject<'pending' | 'loading' | 'loaded' | 'error'>('pending');
  readonly timeZone: string = 'America/New_York';
  readonly _entity$ = this.select((state) => state.entity);
  readonly entity$ = this.select((state) => ({entity: state.entity, getLocal: state.getLocal}));
  readonly setEntity = this.updater((state, value: { entity: EntityInterface | null; getLocal: boolean }) => {
    // console.log('setEntity', state, value);
    return {
      ...state,
      entity: value.entity,
      getLocal: value.getLocal,
    };
  });

  readonly userPresets$ = this.select((state) => state.userPresets).pipe(filter((presets) => !!presets));
  readonly setUserPresets = this.updater((state, userPresets: UserPresetsInterface | null) => ({
    ...state,
    userPresets,
  }));
  readonly lastPunch$ = this.select((state) => state.lastPunch);
  readonly setLastPunch = this.updater((state, lastPunch: LastPunchInterface | null) => ({
    ...state,
    lastPunch,
  }));

  constructor(
    private readonly alert: AlertService,
    private readonly logger: LoggingService,
    private readonly timer: AppTimerService,
    private readonly loadingController: LoadingController,
    private readonly auth: AuthService,
    private readonly network: NetworkService,
    private readonly api: ApiService,
    private readonly storage: StorageService,
    private readonly toast: ToastService,
  ) {
    super({
      entity: null,
      getLocal: true,
      userPresets: null,
      lastPunch: {
        clientId: 0,
        action: 'Clock Out',
        status: 'undefined',
        label: 'Clock In/Out',
        bunit: null,
        projectId: null,
        in: null,
        out: null,
        closed: false,
        approved: false,
      },
    });

    // subscribe to app mode, set from local storage
    this.storage.appMode$.subscribe(mode => {
      this.appMode = mode;
    });

    // loading app online and/or coming back online after being offline
    this.network.onNetworkChange
      .pipe(
        tap((status) => {
          if (status !== undefined) {
            const isOnline = status.status === ConnectionStatus.Online;
            this.isOnline = isOnline;
            this.auth.isOnline$.next(isOnline);
          }
        }),
        pairwise(),
        filter((pairs) => pairs[1].status === ConnectionStatus.Online),
        filter((pairs) => (pairs[0]?.stamp ? pairs[1].stamp - pairs[0].stamp > 10000 : true)),
        tap((pairs) => {
          if (this.auth.isActive$.value && pairs[0].status === ConnectionStatus.Offline) {
            this.overlays.push(this.showOverlay('Checking for un-synced data...'));
          }
        }),
        catchError((err) => {
          this.logger.error('#2.5 catchError', {loc: 'app: 134', err});
          this.clearOverlay('Failed to sync local data! Please contact support. Error: [' + JSON.stringify(err) + ']');
          // this.presentToast("Local data synced to server failed! Error: " + JSON.stringify(err), 3000, "middle").then();
          return EMPTY;
        }),
        switchMap(() =>
          this.auth.isActive$.pipe(
            tap((active) => {
              if (!active) {
                this.clearOverlay();
              }
            }),
            filter((active) => active),
            tap(() => this.logger.information("online, user active", {loc: "app: 147"})),
            switchMap(() =>
              from(this.getEntity()).pipe(
                switchMap((entity) => {
                  if (!!entity && this.overlays.length === 0) {
                    this.loadPeriods().then();
                    if (!entity?.timestamp || Math.abs(this.timer.systemTimer.value - entity.timestamp) > 86400000) {
                      return of(this.loadEmployeeData());
                    }
                    return of(this.setEntity({entity, getLocal: false})); // set getLocal to false Nov 2021
                  } else {
                    return from(this.loadPeriods()).pipe(
                      first(),
                      switchMap(() => !entity
                        ? this.loadEmployeeData(false)
                        : of(undefined)),
                    );
                  }
                }),
              ),
            ),
          ),
        ),
      )
      .subscribe();

    // for loading app offline
    this.network.onNetworkChange
      .pipe(
        filter((status) => status !== undefined),
        first(),
        filter((status) => status.status === ConnectionStatus.Offline),
        switchMap(() =>
          from(this.getEntity()).pipe(
            filter((entity) => !!entity),
            first(),
          ),
        ),
      )
      .subscribe((entity) => {
        this.setEntity({entity, getLocal: true});
      });

    merge(
      this.entity$.pipe(
        // emits when checking for un-synced data
        filter(() => this.overlays.length > 0 && this.isOnline),
        throttleTime(5000),
        tap(() => console.log('employeeEntity emitted, #1: overlay length: ', this.overlays.length)),
      ),
      this.entity$.pipe(
        distinctUntilChanged((a, b) => a.entity?.user.subjectId === b.entity?.user.subjectId),
        filter(({entity}) => !!entity?.user.subjectId && this.isOnline),
        tap((e) => console.log('employeeEntity emitted, #2: ', e)),
      ),
    ).subscribe({
      next: ({getLocal}) => {
        this.loadPresets(getLocal).then();
        this.loadLastPunch().then();

        // ensure system time has been set when app loads, then load the current period's punches
        const interval = setInterval(() => {
          if (!!this.timer.systemTimer.value) {
            clearInterval(interval);
            this.getPeriods(this.timer.systemTimer.value, {start: 3, end: 2})
              .then((periods) => {
                this.getPunches(periods.map((p) => p.number))
                  .then(() => {
                    // this.logger.information("AppStorage getPunches finished", { loc: "app: 215", o: this.overlays.length });
                    if (this.overlays.length > 0) {
                      this.clearOverlay('Local data synced to server successfully!');
                      // this.presentToast("Local data synced to server successfully!", 3000, "middle").then();
                    }
                  })
                  .catch(() =>
                    this.clearOverlay('Failed to sync local data! getPunches failed. Please contact support.'),
                  );
              })
              .catch(() => this.clearOverlay('Failed to sync local data! getPeriods failed. Please contact support.'));
          }
        }, 100);
      },
      error: () => this.clearOverlay('Failed to sync local data! employeeEntity subscribe failed. Please contact support.'),
      complete: () => console.log('employeeEntity subscribe completed.'),
    });
  }

  get punchTypes(): PunchTypesInterface[] {
    return [
      {value: 'punch', name: 'Work', disabled: false},
      {value: 'vacation', name: 'Vacation', disabled: false},
      {value: 'sick', name: 'Sick', disabled: false},
    ];
  }

  getPunchTypeName(val: string): string {
    if (val === 'holiday') {
      return 'Holiday';
    }
    return this.punchTypes.find((t) => t.value === val)?.name ?? null;
  }

  async loadPeriods(): Promise<void> {
    this.periodsLoadingState$.next('loading');
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const years = [year];
      if (month < 3) years.unshift(year - 1);
      if (month > 8) years.push(year + 1);
      for (const year of years) {
        const lastModified = await this.storage.getLastModified(`periods:${year}`);
        this.logger.information('loadPeriods lastModified', {loc: 'app: 260', year, lastModified});
        const resp = (await lastValueFrom(
          this.api.getData<TimesheetPeriodInterface[]>(`periods?type=TS&year=${year}`, {
            includeLastModified: true,
            ifModifiedSince: lastModified,
          })
            .pipe(
              catchError((err: HttpErrorResponse) => {
                if (err.status === 304) {
                  return of(null);
                }
                throw err;
              }),
            ),
        )) as { data: TimesheetPeriodInterface[]; lastModified: string };
        if (resp) {
          await this.storage.setPeriods(year, resp.data);
          await this.storage.setLastModified(`periods:${year}`, resp.lastModified);
          this.periodsLoadingState$.next('loaded');
        }
      }
      // cleanup; remove previous year
      await this.storage.removePeriods(years[0] - 1);
    } catch (e) {
      this.periodsLoadingState$.next('error');
      this.logger.error('loadPeriods catch error', {loc: 'app: 285', error: e});
      await this.alert.present({
        header: 'Periods',
        message: `Oops, there has been a problem loading timesheet periods. Please contact support. Error: [${JSON.stringify(
          e,
        )}]`,
      });
    }
  }

  async getPeriods(
    now: string | number | Date,
    range: { start: number; end: number } = {start: 0, end: 0},
  ): Promise<{ number: number; start: string; end: string; closed: boolean; current: boolean }[] | undefined> {
    await firstValueFrom(this.periodsLoadingState$.pipe(
      filter((state) => state === 'loaded'),
    ));

    const currentTime = utcToZonedTime(now ?? new Date(), this.timeZone);
    const timezoneNow = format(currentTime, 'yyyy-MM-dd', {timeZone: this.timeZone});
    const periods = await this.storage.getPeriods(getYear(currentTime));

    const index = periods
      ? periods.findIndex((p) => timezoneNow >= p.dateFrom.substring(0, 10) && timezoneNow <= p.dateTo.substring(0, 10))
      : undefined;

    const data: { number: number; start: string; end: string; closed: boolean; current: boolean }[] = [];
    if (!!index && index >= 0) {
      const endIndex = index + 1 + range.end;
      for (let i = index - range.start; i < endIndex; i++) {
        if (!!periods[i]) {
          data.push({
            number: periods[i].periodNumber,
            start: zonedTimeToUtc(`${periods[i].dateFrom}T00:00:00`, this.timeZone).toISOString(),
            end: zonedTimeToUtc(`${periods[i].dateTo}T23:59:59`, this.timeZone).toISOString(),
            closed: periods[i].status === 'closed',
            current: i === index,
          });
        }
      }
    } else {
      this.logger.error('AppStorage getPeriods - missing period', {
        loc: 'app: 327',
        data: {now, range, timezoneNow, periods, index, data},
      });
    }
    return data;
  }

  async loadEmployeeData(getLocal: boolean = true): Promise<void> {
    const resno = this.auth.resno;
    if (!!resno) {
      this.logger.information('calling employees endpoint', {loc: 'app: 337', resno});
      await lastValueFrom(
        this.api.getData<EmployeeInterface>(`employees/${resno}`).pipe(
          map((result) => ({
            employee: result['data'] ?? result as EmployeeInterface,
            lastModified: result['lastModified'] as string ?? '',
          })),
          catchError(() => of(null)),
          first(),
          switchMap(async (result) => {
            if (!result) {
              const entity = await this.getEntity();
              const {user, ...employee} = entity ?? {};
              return {user, employee, pass: !!entity};
            }
            const user = this.auth.user$.value;
            return {user, employee: result.employee, pass: true};
          }),
          tap(({user, employee, pass}) => console.log('loadEmployeeData values: ', {user, employee, pass})),
          map(({user, employee, pass}) =>
            !!employee
              ? {
                entity: {
                  user,
                  ...employee,
                  timestamp: this.timer.systemTimer.value,
                },
                pass,
              }
              : {entity: null, pass},
          ),
          tap(({entity}) => {
            if (!!entity) {
              this.setEntity({entity, getLocal});
            }
          }),
          switchMap(({entity, pass}) => (pass ? from(this.confirmEntity(entity)) : of(null))),
        ),
      );
    } else {
      this.logger.error('missing employee resno', {loc: 'app: 377', user: this.auth.user$.value});
      await this.alert.present({
        header: 'Account Setup',
        message: 'Oops, it appears your account resno is missing. Please contact your supervisor or support.',
      });
    }
  }

  private async confirmEntity(entity: EntityInterface): Promise<void> {
    // check and add user to entities array
    const entities = await this.storage.getEntities();
    // look for user in existing data
    const entityIndex = entities.findIndex((e) => e.user.subjectId === entity.user.subjectId);
    if (this.storage.isAppMode$.value && entityIndex < 0 && entities.length === 1) {
      const alert = await this.alert.present({
        header: 'Add or Replace User?',
        message: `Another user is registered on this device.
          Do you want to <u>add</u> or <u>replace</u> this user on this device?</br>
          <b>NOTE: Adding this user will put this device into Kiosk mode.</b>`,
        buttons: [
          {
            text: 'Add',
            handler: () => {
              entities.push(entity);
            },
          },
          {
            text: 'Replace',
            handler: async () => {
              const oldSubjectId = entities[0].user.subjectId;
              entities[0] = entity;
              await this.storage.removeUserData(oldSubjectId);
            },
          },
        ],
      });
      await alert.onDidDismiss();
    } else {
      if (entityIndex < 0) {
        entities.push(entity);
      } else {
        entities[entityIndex] = entity;
      }
    }

    await this.storage.setEntities(entities);
  }

  async getEntity(): Promise<EntityInterface | undefined> {
    const entities = await this.storage.getEntities();
    this.logger.information('getEntity called', {loc: 'app: 427', entities, subjectId: this.auth.subjectId});
    return entities?.find((e) => e.user.subjectId === this.auth.subjectId);
  }

  async removeUser(subjectId: string): Promise<void> {
    await this.storage.removeUser();
    const entities = await this.storage.getEntities();
    // look for user in existing data
    const u = entities.findIndex((entity) => entity.user.subjectId === subjectId);
    if (u >= 0) {
      entities.splice(u, 1);
      await this.storage.setEntities(entities);
    } else {
      await this.alert.present({header: 'Remove User', message: 'No user found to remove.'});
    }
  }

  async getProjects(getLocal: boolean = false): Promise<Project[]> {
    try {
      let projects: Project[];
      projects = await this.storage.getProjects();
      if ((!projects || !getLocal) && this.isOnline) {
        projects = (await lastValueFrom(
          this.api.getData<Project[]>('work-orders'),
        )) as Project[];
        await this.storage.setProjects(projects);
      }

      if (projects) {
        projects.sort((a, b) =>
          (a.businessUnit + ': ' + a.id)
            .toLocaleLowerCase()
            .localeCompare((b.businessUnit + ': ' + b.id).toLocaleLowerCase()),
        );
      }
      const {directClock, defaultWorkOrder} = await firstValueFrom(this.entity$.pipe(
        map(({entity}) => ({
          directClock: entity.directClock,
          defaultWorkOrder: entity.defaultWorkOrder,
        })),
      ));
      return directClock ? projects : projects.filter((p) => p.id === defaultWorkOrder);
    } catch (e) {
      this.logger.error('getProjects error: ', {loc: 'app: 470', error: e});
      return null;
    }
  }

  async loadPresets(getLocal: boolean = false, projects?: Project[]): Promise<void> {
    this.logger.information('loadPresets called ', {loc: 'app: 476', getLocal, projects});
    const subjectId = this.auth.subjectId;
    let presets = await this.storage.getPresets(subjectId);
    if ((!presets || !getLocal) && this.isOnline) {
      try {
        const serverPresets = (await lastValueFrom(
          this.api.getData<UserPresetsInterface>(`user-presets`),
        )) as UserPresetsInterface;
        if (presets && serverPresets && presets.timeStamp > serverPresets.timeStamp) {
          await this.savePresets(presets);
        } else if (serverPresets) {
          if ('userPin' in serverPresets) {// save to server if old data
            presets = this.storage.reshapePresets(serverPresets);
            await this.savePresets(presets);
          } else {
            presets = serverPresets;
            await this.storage.setPresets(subjectId, presets);
          }
        }
      } catch (e) {
        this.logger.error('loadPresets error: ', {loc: 'app: 496', error: e});
        if (e.status !== 404) {
          await this.alert.present({
            header: 'Configuration',
            message: 'Oops, something went wrong getting your configuration. Please contacts support. Error: ' +
              JSON.stringify(e),
          });
        }
      }
    }

    if (presets) {
      if (!projects) {
        projects = await this.getProjects(getLocal);
      }
      presets.data = (presets.data ?? []).filter(({projectId}) => projects.some((p) => p.id === projectId));
      // correct intermittent problem of wrong businessUnit and/or costCenter
      presets.data.forEach((preset) => {
        const {businessUnit, costCenter} = projects.filter((p) => p.id === preset.projectId)[0];
        preset.businessUnit = businessUnit;
        preset.costCenter = costCenter;
      });
      if (presets.data.filter((preset) => preset.value === presets.value).length < 0) {
        presets.value = null;
      }
    }
    if (!presets || presets.data.length === 0) {
      // moved alert to home page, so it shows every time user comes back to home page
      // await this.alert.present({header: 'Configuration', message: 'It appears your configuration is incomplete. You will need to configure your projects before you can punch time.'});
      presets = {value: null, data: [], timeStamp: null};
    }
    this.setUserPresets(presets);
  }

  async savePresets(presets: UserPresetsInterface): Promise<void> {

    try {
      if (this.isOnline) {
        await lastValueFrom(
          this.api.postData<UserPresetsInterface, void>(`user-presets`, {
            ...presets,
          }),
        );
      }
      this.setUserPresets(presets);
      await this.storage.setPresets(this.auth.subjectId, presets);
    } catch (e) {
      await this.alert.present({
        header: 'Configuration',
        message: `Oops, something went wrong saving your configuration. Please contact support. Error: ${JSON.stringify(e)}`,
      });
    }
  }

  validatePunchTimes(
    punch: ValidatePunchTimesInterface,
    punches: PunchInterface[],
    displayMsg: boolean = true,
  ): boolean {
    const punchTypes = punch.punchType !== 'punch' ? ['vacation', 'holiday', 'sick'] : ['punch'];
    const sysTime = roundToNearestMinutes(new Date(punch.systemTime));
    const a = {
      start: roundToNearestMinutes(new Date(punch.inDateTime)),
      end: punch.outDateTime === null ? sysTime : roundToNearestMinutes(new Date(punch.outDateTime)),
      open: punch.outDateTime === null,
    };

    for (var i = 0; i < punches.length; i++) {
      if (
        punchTypes.indexOf(punches[i].punchType) >= 0 &&
        !punches[i].isDeleted &&
        (punch.id > 0 ? punch.id !== punches[i].id : punch.clientId !== punches[i].clientId)
      ) {
        const b = {
          start: roundToNearestMinutes(new Date(punches[i].inDateTime)),
          end: punches[i].outDateTime === null ? sysTime : roundToNearestMinutes(new Date(punches[i].outDateTime)),
          open: punches[i].outDateTime === null,
        };
        if ((a.start < b.end && b.start < a.end) || (a.open && b.open)) {
          break;
        }
      }
    }
    if (i < punches.length) {
      // validation failed
      this.logger.warning('punch validation failed', {loc: 'app: 581', a: punch, b: punches[i]});
      if (displayMsg) {
        const ptn = this.getPunchTypeName(punches[i].punchType);
        const atn = this.getPunchTypeName(punch.punchType);
        const header = punch.punchType !== 'punch' ? `Record ${atn} Time` : 'Save Punch';
        this.alert.present({
          header,
          message: `Oops, you have ${(ptn !== atn ? 'a ' : 'another ') + ptn}
          punch that conflicts with this ${ptn !== atn ? atn + ' punch' : ' one'}.
          Please check your entries and try again.`,
        }).then();
      }
      return false;
    } else {
      // validation passed
      return true;
    }
  }

  async validatePTOPunch(data: {
    ptoDate: Date;
    ptoHours: string;
    ptoDays: number;
    punchData: {
      ptoType: string;
      id: number;
      clientId: number;
    };
    week: {
      periodNumber: number;
      start: string;
      end: string;
    };
  }): Promise<void | {
    punches: PunchInterface[];
    id: number;
    sdt: string;
    edt: string;
    businessUnit: string;
    costCenter: string;
    projectId: string;
    ptoDays: { in: string; out: string }[];
  }> {
    const ptoTypeName = this.getPunchTypeName(data.punchData.ptoType);
    const ptoDays: { in: string; out: string }[] = [];

    if (
      isNaN(data.ptoDate.getTime()) ||
      data.ptoDate.toISOString() < data.week.start ||
      data.ptoDate.toISOString() > data.week.end
    ) {
      await this.alert.present({
        header: `Record ${ptoTypeName} Day`,
        message: `Invalid date. Please select the ${ptoTypeName} Date.`,
      });
      return;
    }

    if (!data.ptoHours) {
      await this.alert.present({
        header: `Record ${ptoTypeName} Day`,
        message: `Please select the duration for this ${ptoTypeName} day.`,
      });
      return;
    }

    const ptoHours = data.ptoHours === 'range' ? ['8', '16'] : data.ptoHours.split('-');
    const inDateTime = zonedTimeToUtc(setHours(data.ptoDate, +ptoHours[0]), this.timeZone).toISOString();
    const outDateTime = zonedTimeToUtc(setHours(data.ptoDate, +ptoHours[1]), this.timeZone).toISOString();

    const timeSheet = (await this.getPunches([data.week.periodNumber], false))[0];
    // check for other conflicting punches
    const punch = {
      id: data.punchData.id,
      clientId: data.punchData.clientId,
      punchType: data.punchData.ptoType,
      inDateTime,
      outDateTime,
      systemTime: null,
    };

    let isValid = this.validatePunchTimes(punch, timeSheet.punches);
    let totalHours = +ptoHours[1] - +ptoHours[0];
    for (let i = 2; isValid && i <= data.ptoDays; i++) {
      totalHours += +ptoHours[1] - +ptoHours[0];
      data.ptoDate.setDate(data.ptoDate.getDate() + 1);
      const inDT = zonedTimeToUtc(setHours(data.ptoDate, +ptoHours[0]), this.timeZone).toISOString();
      const outDT = zonedTimeToUtc(setHours(data.ptoDate, +ptoHours[1]), this.timeZone).toISOString();
      isValid = this.validatePunchTimes(
        {
          id: data.punchData.id,
          clientId: data.punchData.clientId,
          punchType: data.punchData.ptoType,
          inDateTime: inDT,
          outDateTime: outDT,
          systemTime: null,
        },
        timeSheet.punches,
      );
      if (isValid) {
        ptoDays.push({in: inDT, out: outDT});
      } else {
        break;
      }
    }

    if (isValid) {
      const entity = await firstValueFrom(
        this.entity$.pipe(
          map((v) => v.entity),
        ),
      );

      if (
        !(await this.ptoEligibleCheck(
          entity,
          data,
          inDateTime,
          ptoTypeName,
          totalHours))
      ) {
        return;
      }

      const defaultWorkOrder = await firstValueFrom(this.entity$.pipe(
        map(({entity}) => entity.defaultWorkOrder),
      ));
      const workOrderId = data.punchData.ptoType === 'vacation' ? 'VAC' : defaultWorkOrder;

      return {
        punches: timeSheet.punches,
        id: new Date(inDateTime).getTime(),
        sdt: inDateTime,
        edt: outDateTime,
        projectId: workOrderId,
        businessUnit: 'PTO',
        costCenter: 'PTO',
        ptoDays,
      };
    }
  }

  private async ptoEligibleCheck(
    entity: EntityInterface,
    data: {
      ptoDate: Date;
      ptoHours: string;
      ptoDays: number;
      punchData: {
        ptoType: string;
        id: number;
        clientId: number;
      };
      week: {
        periodNumber: number;
        start: string;
        end: string;
      };
    },
    inDateTime: string,
    ptoTypeName: string,
    totalHours: number,
  ): Promise<boolean> {
    if (
      (data.punchData.ptoType === 'vacation' && inDateTime < new Date(entity.vacationEligibility).toISOString()) ||
      (data.punchData.ptoType === 'sick' && inDateTime < new Date(entity.sickTimeEligibility).toISOString())
    ) {
      await this.alert.present({
        header: `Record ${ptoTypeName} Day`,
        message: `Oops, it appears you are not eligible for ${ptoTypeName} time yet.`,
      });
      return false;
    } else {
      // check for PTO punches not synced
      const timeSheets = await this.getPunches(undefined, false);
      const ptoHoursTaken = this.flattenPunches(timeSheets)
        .filter((punch) => !punch.id && punch.punchType === data.punchData.ptoType && data.punchData.clientId !== punch.clientId && !punch.isDeleted)
        .map(({inDateTime, outDateTime}) => differenceInHours(new Date(inDateTime), new Date(outDateTime)))
        .reduce((prev, curr) => prev + curr, 0);
      const ptoBalance = data.punchData.ptoType === 'vacation' ? (entity.vacationBalance - ptoHoursTaken)
        : (data.punchData.ptoType === 'sick' ? (entity.sickTimeBalance - ptoHoursTaken) : 0);
      this.logger.information('ptoEligibleCheck: ', {loc: 'app: 762', ptoTypeName, ptoHoursTaken, ptoBalance});
      if (totalHours > ptoBalance) {
        await this.alert.present({
          header: `Record ${ptoTypeName} Day`,
          message: `Oops, it appears you do not have enough ${ptoTypeName} time to record this paid time off.</br>
            NOTE: You have ${ptoBalance} hours of ${ptoTypeName} time available and are trying to enter ${totalHours} hours.`,
        });
        return false;
      }
    }

    return true;
  }

  async getPunches(
    periodNumbers?: number[],
    setLocal: boolean = true,
  ): Promise<{ periodNumber: number; closed: boolean; punches: PunchInterface[] }[]> {
    const subjectId = this.auth.subjectId;
    let pData = await this.storage.getPunches(subjectId);
    if (this.isOnline && !!periodNumbers) {
      if (pData) {
        await this.setServerPunchesIfNeeded(
          this.flattenPunches(pData),
        );
      }
      pData = await this.getServerPunches(periodNumbers);
    }

    if (this.isOnline && setLocal) {
      await this.storage.setPunches(subjectId, pData ?? null);
    }

    return !!periodNumbers ? pData.filter((p) => periodNumbers.includes(p.periodNumber)) : pData;
  }

  async savePunches(
    punches: PunchInterface[],
    period: { number: number; closed: boolean },
    sendToServer: boolean = true,
  ): Promise<void> {
    // this.logger.information(`savePunches called`, { loc: 'app: 803', punches, period, sendToServer });
    try {
      const subjectId = this.auth.subjectId;
      const pData = await this.storage.getPunches(subjectId);
      // this.logger.information(`savePunches, ready to run forEach`, { loc: "app: 807", punches, pData });
      punches.forEach((punch) => {
        const idx = pData.findIndex(
          (p: {
            closed: boolean;
            punches: PunchInterface[];
            periodNumber: number
          }) => p.periodNumber === punch.periodNumber,
        );
        if (idx < 0) {
          pData.push({closed: period.closed, periodNumber: punch.periodNumber, punches: [punch]});
        } else {
          const i = pData[idx].punches.findIndex((p: PunchInterface) =>
            punch.id > 0 ? punch.id === p.id : punch.clientId === p.clientId,
          );
          if (i < 0) {
            pData[idx].punches.push(punch);
          } else {
            pData[idx].punches[i] = punch;
          }
        }
      });

      // sort periods by period number and then punches by inDateTime (timestamp)
      pData.sort((a, b) => a.periodNumber - b.periodNumber)
        .forEach(({punches}) => {
          punches.sort((a, b) => new Date(a.inDateTime).getTime() - new Date(b.inDateTime).getTime());
        });

      this.loadLastPunch(pData).then();
      await this.storage.setPunches(subjectId, pData);

      // only save if online and periodNumber is supplied
      if (this.isOnline && sendToServer) {
        // online so save to server and then update data to local storage
        this.setServerPunchesIfNeeded(
          this.flattenPunches(pData),
          [period.number],
        ).then();
      }
    } catch (e) {
      this.logger.error(`Error saving punches`, {loc: 'app: 848', error: e, punches});
      await this.alert.present({
        header: 'Save Punches',
        message: 'Oops, something went wrong saving your punches! Please contact support. Error: ' + JSON.stringify(e),
      });
    }
  }

  async getLastPunch(): Promise<{ punch: PunchInterface; closed: boolean }> {
    let pdata: { punch: PunchInterface; closed: boolean };
    if (this.isOnline) {
      try {
        pdata = (await lastValueFrom(
          this.api.getData<{ punch: PunchInterface; closed: boolean }>(`punches/last`),
        )) as { punch: PunchInterface; closed: boolean };
        if (!pdata) {
          pdata = {punch: null, closed: false};
        }
      } catch (e) {
        await this.alert.present({
          header: 'Last Punch',
          message: 'Oops, something went wrong getting your last punch! Please contact support. Error: ' + JSON.stringify(e),
        });
      }
    } else {
      const subjectId = this.auth.subjectId;
      const timeSheets = await this.storage.getPunches(subjectId);
      const punches = this.flattenPunches(timeSheets ?? []).filter((p) => p.punchType === 'punch' && !p.isDeleted);
      const i = punches.length - 1;
      if (i >= 0) {
        const {closed, ...punch} = punches[i];
        pdata = {punch, closed};
      } else {
        pdata = {punch: null, closed: false};
      }
    }
    return pdata;
  }

  async loadLastPunch(timeSheets?: TimeSheet[]): Promise<void> {
    let lastPunch: { punch: PunchInterface; closed: boolean };
    if (!!timeSheets) {
      const punches = this.flattenPunches(timeSheets)
        .filter((p) => p.punchType === 'punch' && !p.isDeleted);
      const i = punches.length - 1;
      if (i >= 0) {
        const {closed, ...punch} = punches[i];
        lastPunch = {punch, closed};
      } else {
        lastPunch = {punch: null, closed: false};
      }
    } else {
      lastPunch = await this.getLastPunch();
      if (lastPunch === undefined) return;
    }
    // this.logger.information("loadLastPunch", { loc: "app: 903", lastPunch });

    if (!!lastPunch?.punch) {
      const isClockedIn = lastPunch.punch.inDateTime !== null && lastPunch.punch.outDateTime === null;
      this.setLastPunch({
        clientId: lastPunch.punch.clientId,
        action: isClockedIn ? 'Clock In' : 'Clock Out',
        status: isClockedIn ? 'Clocked In' : 'Clocked Out',
        label: isClockedIn ? 'Clock Out' : 'Clock In',
        bunit: lastPunch.punch.businessUnit,
        projectId: lastPunch.punch.projectId,
        in:
          lastPunch.punch.inDateTime !== null
            ? roundToNearestMinutes(new Date(lastPunch.punch.inDateTime)).toISOString()
            : null,
        out:
          lastPunch.punch.outDateTime !== null
            ? roundToNearestMinutes(new Date(lastPunch.punch.outDateTime)).toISOString()
            : null,
        closed: lastPunch.closed,
        approved: lastPunch.punch.isUserApproved,
      });
    } else {
      // brand-new user, no previous punches
      // noinspection SpellCheckingInspection
      this.setLastPunch({
        clientId: 0,
        action: 'Clock Out',
        status: 'undefined',
        label: 'Clock In',
        bunit: null,
        projectId: null,
        in: null,
        out: null,
        closed: false,
        approved: false,
      });
    }
  }

  private flattenPunches(timeSheets: TimeSheet[]): (PunchInterface & { closed: boolean })[] {
    const punches: (PunchInterface & { closed: boolean })[] = [];
    timeSheets.forEach((timeSheet) => {
      if (timeSheet?.punches) {
        punches.push(...timeSheet.punches.map((p) => ({...p, closed: timeSheet.closed})));
      }
    });
    return punches;
  }

  async getTimesheet(periodNumber: number): Promise<TimesheetInterface | undefined> {
    try {
      const timesheet = (await lastValueFrom(
        this.api.getData<TimesheetInterface>(`timesheets/${periodNumber}`),
      )) as TimesheetInterface;
      return ({periodNumber, ...timesheet});
    } catch (e) {
      if (e.status === 404) {
        await this.toast.present('Timesheet not found for this period. periodNumber: ' + periodNumber);
      } else {
        await this.alert.present({
          header: 'Get Timesheet',
          message: `Oops, something went wrong getting timesheet data. Error: [${JSON.stringify(e)}]`,
        });
      }
      return undefined;
    }
  }

  async closeTimesheet(periodNumber: number): Promise<TimesheetInterface | undefined> {
    try {
      return await lastValueFrom(this.api.postData<any, undefined>(`timesheets/${periodNumber}/close`, {}));
    } catch (e) {
      if (e.status === HttpStatusCode.NotFound) {
        await this.toast.present('Timesheet not found for this period. periodNumber: ' + periodNumber);
      } else {
        await this.alert.present({
          header: 'Close Timesheet',
          message: `Oops, something went wrong closing your timesheet. Error: [${JSON.stringify(e)}]`,
        });
      }
      return undefined;
    }
  }

  private async setServerPunchesIfNeeded(
    punches: PunchInterface[],
    periodNumbers?: number[],
  ): Promise<void> {
    const punchesToSet = punches.filter((punch) => punch.id === 0 || punch.update);
    if (punchesToSet.length > 0) {
      await this.setServerPunches(punchesToSet, periodNumbers);
    }
  }

  private async setServerPunches(
    punches: PunchInterface[],
    periodNumbers?: number[],
  ): Promise<void> {
    try {
      const camLocation = await firstValueFrom(
        this.entity$.pipe(
          map(({entity}) => entity.camLocation),
        ));
      await lastValueFrom(
        this.api.postData<PunchInterface[], void>(
          `punches`,
          punches.map((punch: PunchInterface) => ({...punch, camLocation})),
        ),
      );

      // update user data for sick and vacation punches
      if (punches.filter((sp) => sp.punchType !== 'punch').length > 0) {
        this.loadEmployeeData().then();
      }

      // get punches and update local storage on condition
      if (!!periodNumbers) {
        const serverPunches = await this.getServerPunches(periodNumbers);
        const subjectId = this.auth.subjectId;

        if (!!subjectId && !!serverPunches) {
          this.loadLastPunch(serverPunches).then();
          this.storage.setPunches(subjectId, serverPunches).then();
        }
      }
    } catch (e) {
      this.logger.error(`setServerPunches Error`, {loc: 'app: 1030', error: e.error});
      await this.alert.present({
        header: 'Save Punches Error',
        message: 'Error from savePunches: ' + (typeof e.error.message === 'string' ? e.error.message : JSON.stringify(e.error)),
      });
    }
  }

  async getServerPunches(periodNumbers: number[]): Promise<{
    punches: PunchInterface[];
    closed: boolean;
    periodNumber: number
  }[] | undefined> {
    return await lastValueFrom(
      forkJoin(
        periodNumbers.map((periodNumber) =>
          this.api.getData<{ punches: PunchInterface[]; closed: boolean }>(`punches/${periodNumber}`)
            .pipe(
              map((res) => res as { punches: PunchInterface[]; closed: boolean }),
              map((punchObj) => ({
                closed: punchObj.closed,
                punches: punchObj.punches.sort((a, b) => new Date(a.inDateTime).getTime() - new Date(b.inDateTime).getTime()),
                periodNumber,
              })),
            ),
        ),
      ),
    );
  }

  showOverlay(msg: string): Promise<any> {
    return this.loadingController
      .create({
        message: msg,
      })
      .then(async (o) => {
        await o.present();
        return o;
      });
  }

  private clearOverlay(msg?: string): void {
    this.overlays.forEach((p) => {
      p.then((o) => o.dismiss());
    });
    this.overlays = [];
    if (msg) this.toast.present(msg, 3000, 'middle').then();
  }
}

export interface TimeSheet {
  periodNumber?: number;
  closed: boolean;
  punches: PunchInterface[];
}
